import { type ReactNode } from "react";

import { Tooltip } from "antd";
import { clsx } from "clsx";

import { BadgeStatus, BADGE_STATUS, LoadingTitleSize, RVLoadingTitle, RVStatusBadge } from "../../atoms";
import { RVRow } from "../../layouts/RVFlexbox/RVFlexbox";

import styles from "./RVStatusDisplay.module.scss";

const DEFAULT_VERSION_TOOLTIP_TEXT = "Version History";

/**
 * Props for the RVStatusDisplay component.
 */
type RVStatusDisplayProps = {
    /** Entity ID to display */
    id?: string | number;
    /** Badge status type (e.g., ACTIVE, PENDING, INACTIVE) */
    status?: BadgeStatus;
    /** Content to display in the status badge */
    content?: string | JSX.Element;
    /** Whether the component is in a loading state */
    loading?: boolean;
    /** Test ID for testing purposes */
    "data-testid"?: string;
    /** Whether the version number is clickable */
    isVersionClickable?: boolean;
    /** Callback function when version number is clicked */
    onVersionClick?: () => void;
    /** Version number to display (typically from route params) */
    version?: string;
    /** Tooltip text for the version */
    versionTooltipText?: string;
    /** Traffic allocation percentage to display (0–100) */
    trafficAllocation?: number;
    /** Callback when the traffic split label is clicked */
    onTrafficSplitClick?: () => void;
    /** Optional node rendered between the ID/version block and the status badge */
    statusExtra?: ReactNode;
    /**
     * Visual variant.
     * - `default`: standard header status display.
     * - `document`: form / document-layout styling (e.g. copilot workbench).
     * Version clickability is controlled by `isVersionClickable` in both variants.
     */
    variant?: "default" | "document";
};

/**
 * Displays entity status information including ID, version, and status badge.
 *
 * This component shows:
 * - Entity ID number
 * - Version number (if provided) with optional click handler
 * - Status badge with customizable content and status type
 * - Loading state with skeleton UI
 *
 * The component returns an empty fragment if no ID is provided and not in loading state.
 *
 * @example
 * ```tsx
 * <RVStatusDisplay
 *   id={12345}
 *   version="3"
 *   status={BADGE_STATUS.ACTIVE}
 *   content="Active"
 *   onVersionClick={() => console.log("Version clicked")}
 * />
 * ```
 */
export function RVStatusDisplay({
    id,
    status,
    content = "Draft",
    loading = false,
    isVersionClickable = true,
    onVersionClick,
    "data-testid": dataTestId,
    version,
    versionTooltipText = DEFAULT_VERSION_TOOLTIP_TEXT,
    trafficAllocation,
    onTrafficSplitClick,
    statusExtra,
    variant = "default",
}: RVStatusDisplayProps): JSX.Element {
    if (!id && !loading) {
        return <></>;
    }

    const versionIsClickable = isVersionClickable && !!onVersionClick;

    return loading ? (
        <RVLoadingTitle size={LoadingTitleSize.MEDIUM} />
    ) : (
        <RVRow
            className={clsx(styles.root, {
                [styles.document]: variant === "document",
                [styles.hasStatusExtra]: !!statusExtra,
            })}
            alignItems="center"
            data-testid={dataTestId}
        >
            <div className={styles.idVersionContainer}>
                <span className={styles.id} data-testid={`${dataTestId}-id`}>
                    ID # {id}
                </span>

                {version && (
                    <>
                        <div className={styles.spacer} />
                        {versionIsClickable ? (
                            <Tooltip title={versionTooltipText} placement="bottom">
                                <button type="button" className={styles.version} onClick={onVersionClick}>
                                    Version {version}
                                </button>
                            </Tooltip>
                        ) : (
                            <span>Version {version}</span>
                        )}
                    </>
                )}
                {trafficAllocation !== undefined && trafficAllocation !== null && (
                    <span>
                        {"Traffic Split: "}
                        <span
                            className={clsx({ [styles.version]: !!onTrafficSplitClick && variant !== "document" })}
                            onClick={variant === "document" ? undefined : onTrafficSplitClick}
                        >
                            {trafficAllocation}%
                        </span>
                    </span>
                )}
            </div>
            {statusExtra && (
                <>
                    <div className={styles.spacer} />
                    <div className={styles.statusExtra}>{statusExtra}</div>
                    <div className={styles.spacer} />
                </>
            )}
            {status && <RVStatusBadge status={status || BADGE_STATUS.VIEW_ONLY} content={content || "View Only"} />}
        </RVRow>
    );
}
